import { apiService } from './apiService';
import { signalRService } from '../signalr/SignalRService';
import { HubMethods } from '../signalr/types';

/** A saved/visited room row for the invite picker (P3). Mirrors backend RoomSummary. */
export interface MyRoomSummary {
  id: string;
  name: string;
  code: string;
  category: string;
  subCategory?: string | null;
  sceneTemplateId?: string | null;
  currentPlayers: number;
  maxPlayers: number;
  hasPassword: boolean;
  requiresApproval: boolean;
  lastVisitAt?: string | null;
  saved: boolean;
}

export interface MyRoomsList {
  saved: MyRoomSummary[];
  visited: MyRoomSummary[];
}

/** Spawn the invited pair lands on (the inviter's last spot in the target room). */
export interface TravelSpawn {
  x: number;
  y: number;
  z: number;
  rotY: number;
  zoneId: string;
  configId: string;
}

class RoomInviteService {
  /** The current user's saved (top) + visited (recency) rooms. */
  async getMyRooms(): Promise<MyRoomsList> {
    const res = await apiService.get<MyRoomsList>('/api/rooms/mine/list');
    if (res.error || !res.data) return { saved: [], visited: [] };
    return res.data;
  }

  async saveRoom(roomId: string): Promise<boolean> {
    const res = await apiService.post<{ saved: boolean }>(`/api/rooms/${roomId}/save`);
    return !res.error && !!res.data?.saved;
  }

  async unsaveRoom(roomId: string): Promise<boolean> {
    const res = await apiService.delete<{ saved: boolean }>(`/api/rooms/${roomId}/save`);
    return !res.error;
  }

  // ---- SocialEvent wire (stringified outer envelope; server validates + relays) ----

  private async sendEnvelope(currentRoomId: string, type: string, payload: Record<string, unknown>): Promise<{ ok: boolean; [k: string]: unknown }> {
    try {
      const envelopeJson = JSON.stringify({ type, payloadJson: JSON.stringify(payload) });
      const json = await signalRService.invoke<string>(HubMethods.SocialEvent, currentRoomId, envelopeJson);
      return typeof json === 'string' ? JSON.parse(json) : (json as { ok: boolean });
    } catch (e) {
      console.error(`[RoomInvite] ${type} failed:`, e);
      return { ok: false };
    }
  }

  /** Invite a player (in the current room) to travel to another room. */
  sendInvite(currentRoomId: string, targetUserId: string, targetRoomId: string, targetRoomName: string) {
    return this.sendEnvelope(currentRoomId, 'room_invite', {
      targetUserId,
      roomId: targetRoomId,
      roomName: targetRoomName,
    });
  }

  respondToInvite(currentRoomId: string, inviteId: string, accept: boolean) {
    return this.sendEnvelope(currentRoomId, accept ? 'room_invite_accept' : 'room_invite_decline', { inviteId });
  }
}

export const roomInviteService = new RoomInviteService();
